import React, { useCallback, useRef, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Form, Input, Modal, Spin, Typography } from 'antd';
import type { InputRef } from 'antd';

import { tap } from 'rxjs/operators';
import type { Group } from 'v2/interfaces/access-control';
import accessControlService from 'v2/services/access-control';

import FormGroupModule from './Module';

type Props = {
  current?: Group;
  onComplete: () => void;
  onCancel: () => void;
};

export default function FormGroup({ onCancel, onComplete, current }: Props) {
  const { message } = App.useApp();
  const [isLoadingModules] = useObservable(() => accessControlService.isLoadingModules$, []);
  const [modules] = useObservable(() => accessControlService.getModules(), []);
  const [groupName, setGroupName] = useState(current?.name || '');
  const [selectedActionsId, setSelectedActionsId] = useState(() => {
    if (!current) return [];
    const actionsData = Object.values(current.privileges).flatMap(privilege => privilege.actions);
    return actionsData.map(action => action.id);
  });
  const [isSaving, setIsSaving] = useState(false);

  const [saveGroup] = useCallbackObservable(
    data =>
      accessControlService.saveGroup(data).pipe(
        tap(
          () => {
            message.success('Sucesso, grupo criado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            message.error(typeof err === 'string' ? err : 'Não foi possível criar o grupo.');
            setIsSaving(false);
          }
        )
      ),
    [message, onComplete]
  );

  const [updateGroup] = useCallbackObservable(
    data =>
      accessControlService.updateGroup(data).pipe(
        tap(
          () => {
            message.success('Sucesso, grupo atualizado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            message.error(typeof err === 'string' ? err : 'Não foi possível atualizar o grupo.');
            setIsSaving(false);
          }
        )
      ),
    [message, onComplete]
  );

  const groupNameInputRef = useRef<InputRef>(null);

  const isEditing = !!current;

  function handleModalSubmit() {
    setIsSaving(true);

    if (!groupName) {
      groupNameInputRef.current?.focus();
      message.error('O nome do grupo é obrigatório.');
      setIsSaving(false);
      return;
    }

    if (!selectedActionsId.length) {
      message.error('O grupo precisa ter pelo menos uma ação.');
      setIsSaving(false);
      return;
    }

    if (isEditing) {
      updateGroup({
        id: current.id,
        name: groupName,
        actions: selectedActionsId
      });
      return;
    }

    saveGroup({
      name: groupName,
      actions: selectedActionsId
    });
  }

  const handleActionsChange = useCallback((id: number) => {
    setSelectedActionsId(old => {
      if (old.includes(id)) {
        return old.filter(actionId => actionId !== id);
      }

      return [...old, id];
    });
  }, []);

  return (
    <Modal
      open
      width={720}
      destroyOnClose
      onCancel={onCancel}
      title={isEditing ? 'Editar grupo' : 'Novo grupo'}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={onCancel} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type='primary' loading={isSaving || isLoadingModules} onClick={handleModalSubmit}>
            {isEditing ? 'Salvar alterações' : 'Adicionar grupo'}
          </Button>
        </div>
      }
    >
      <div className='mb-5 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
        <Typography.Text strong className='block'>
          Grupo de ações
        </Typography.Text>
        <Typography.Text type='secondary' className='text-sm'>
          Defina o nome e selecione os módulos e ações disponíveis neste grupo.
        </Typography.Text>
      </div>

      {isLoadingModules && (
        <div className='flex justify-center py-8'>
          <Spin />
        </div>
      )}

      {!isLoadingModules && (
        <Form layout='vertical' className='flex flex-col gap-4'>
          <Form.Item
            label='Nome do grupo'
            required
            className='mb-0'
            help='Identifique o grupo para facilitar a gestão de acessos'
          >
            <Input
              ref={groupNameInputRef}
              value={groupName}
              disabled={isSaving}
              onChange={e => setGroupName(e.target.value)}
              placeholder='Ex.: Gerentes comerciais'
              maxLength={80}
              showCount
            />
          </Form.Item>

          <div>
            <Typography.Text strong className='mb-1 block'>
              Permissões por módulo
            </Typography.Text>
            <Typography.Text type='secondary' className='mb-3 block text-sm'>
              Selecione ao menos uma ação para liberar o acesso
            </Typography.Text>

            <div className='flex max-h-[50vh] flex-col gap-2 overflow-y-auto pr-1'>
              {!!modules?.length &&
                modules.map((module, index) => (
                  <FormGroupModule
                    key={module.id}
                    handleActionsChange={handleActionsChange}
                    selectedActionsId={selectedActionsId}
                    module={module}
                    initialOpen={index === 0}
                  />
                ))}
            </div>
          </div>
        </Form>
      )}
    </Modal>
  );
}
